import { useApolloClient } from '@apollo/client';
import type { Tokens } from '@ovComponents/0-tokens/useTokens';
import type {
  DataTableTanstackTable,
  RowsPerPageOption,
} from '@ovComponents/2-component/dataTable';
import { UserContext } from '@providers/userContextProvider';
import type { TFunction } from 'i18next';
import { useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useReconFlagsKpiCounts } from '../dashboard/useReconFlagsKpiCounts';
import type { ReconFlagsFiltersCardProps } from '../filters/reconFlagsFiltersCard.types';
import type { ReconFlagRow } from './reconFlags.types';
import { useReconFlagsDrawer } from './useReconFlagsDrawer';
import { useReconFlagsFilters } from './useReconFlagsFilters';
import { useReconFlagsGrid } from './useReconFlagsGrid';
import { useReconFlagsPagination } from './useReconFlagsPagination';

export type UseReconFlagsPageResult = {
  t: TFunction;
  sys: Tokens['sys'];
  orgId: string | undefined;
  kpiCounts: Record<string, number>;
  spotlightProps: {
    sys: Tokens['sys'];
    kpiCounts: Record<string, number>;
    orgTotalFlags: number;
    unresolvedThreePlusDisplay: number;
  };
  filtersProps: ReconFlagsFiltersCardProps;
  gridProps: {
    sys: Tokens['sys'];
    table: DataTableTanstackTable<ReconFlagRow>;
    pageSize: RowsPerPageOption;
    tableLoading: boolean;
    pageCount: number;
    page: number;
    onPageChange: (p: number) => void;
    totalRowCount: number;
    onPerPageChange: (perPage: number) => void;
    onRowClick: (row: ReconFlagRow) => void;
    selectedCount: number;
    onSelectAllOnPage: () => void;
    onClearSelection: () => void;
    onBulkDismiss: () => Promise<boolean>;
    bulkDismissLoading: boolean;
    gridQueryLoading: boolean;
  };
  drawerProps: {
    sys: Tokens['sys'];
    flag: ReconFlagRow | null;
    isOpen: boolean;
    onClose: () => void;
    notesDraft: string;
    onNotesDraftChange: (next: string) => void;
    onNotesSaved: (notes: ReconFlagRow['notes']) => void;
    onResolve: () => Promise<void>;
    onDismiss: () => Promise<void>;
    resolveLoading: boolean;
    dismissLoading: boolean;
  };
};

export const useReconFlagsPage = (
  sys: Tokens['sys'],
): UseReconFlagsPageResult => {
  const { t } = useTranslation(['reconciliation']);
  const { activeOrganization } = useContext(UserContext);
  const orgId = activeOrganization?.id;
  const client = useApolloClient();

  const { page, setPage, pageSize, setPageSize } = useReconFlagsPagination();

  const {
    kpiCounts,
    orgTotalFlags,
    unresolvedThreePlusCount,
    refetch: refetchKpis,
  } = useReconFlagsKpiCounts(orgId);
  const refetchKpisRef = useRef(refetchKpis);
  refetchKpisRef.current = refetchKpis;

  const {
    gridFilter,
    skipFlagsForAccountSearch,
    accountSearchInFlight,
    filtersProps,
  } = useReconFlagsFilters({ orgId, setPage });

  const refetchGridRef = useRef<() => Promise<unknown>>(async () => undefined);

  const refetchGrid = useCallback(() => refetchGridRef.current(), []);

  const refetchAfterFlagMutation = useCallback(async () => {
    client.cache.evict({ fieldName: 'fetchFlagsForGrid' });
    client.cache.gc();
    await Promise.all([refetchGridRef.current(), refetchKpisRef.current()]);
  }, [client]);

  const { drawerProps, openDrawer } = useReconFlagsDrawer({
    sys,
    t,
    refetchAfterFlagMutation,
    refetchGrid,
  });

  const { gridProps, refetch } = useReconFlagsGrid({
    sys,
    t,
    orgId,
    gridFilter,
    flagState: filtersProps.flagState,
    skipFlagsForAccountSearch,
    accountSearchInFlight,
    page,
    setPage,
    pageSize,
    setPageSize,
    onRowClick: openDrawer,
    refetchAfterFlagMutation,
  });
  refetchGridRef.current = refetch;

  return {
    t,
    sys,
    orgId,
    kpiCounts,
    spotlightProps: {
      sys,
      kpiCounts,
      orgTotalFlags,
      unresolvedThreePlusDisplay: unresolvedThreePlusCount,
    },
    filtersProps: { sys, ...filtersProps },
    gridProps,
    drawerProps,
  };
};
